import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/features/auth/store";
import { useLogout } from "@/features/auth/use-logout";
import {
  useMarkNotificationsRead,
  useNotifications,
  useUnreadCount,
} from "@/hooks/use-notifications";
import { cn } from "@/lib/cn";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  BarChart3,
  Bell,
  Database,
  LayoutDashboard,
  Link2,
  LogOut,
  MonitorDot,
  UserCog,
  Users,
} from "lucide-react";
import { forwardRef } from "react";

type NavSection = {
  to: string;
  label: string;
  Icon: typeof LayoutDashboard;
};

// IR-1047: Join Links is visible in BOTH dev and prod (the IR-922 prod-only
// gate was removed). Staff-auth is still enforced by the `_authed` layout.
const BASE_SECTIONS: readonly NavSection[] = [
  { to: "/", label: "Overview", Icon: LayoutDashboard },
  { to: "/monitoring", label: "Monitoring", Icon: MonitorDot },
  { to: "/data", label: "Data Management", Icon: Database },
  { to: "/links", label: "Join Links", Icon: Link2 },
  { to: "/analytics", label: "Analytics", Icon: BarChart3 },
  { to: "/presence", label: "Presence", Icon: Users },
  { to: "/users", label: "Users", Icon: UserCog },
] as const;

export function TopNav() {
  const { location } = useRouterState();
  const sections = BASE_SECTIONS;
  return (
    <header
      className="flex h-16 items-center justify-between px-6 pb-9 mt-[20px]"
      style={{
        opacity: 0,
        transform: "translateY(-8px)",
        animation: "fadeDown 0.8s cubic-bezier(.2,.8,.2,1) 0.1s forwards",
      }}
    >
      {/* Brand */}
      <div className="flex items-center gap-3">
        <div
          aria-hidden
          className="relative h-[34px] w-[34px] rounded-full"
          style={{
            background:
              "conic-gradient(from 210deg, #06b6d4 0%, #8b5cf6 35%, #e879f9 55%, #f472b6 75%, #06b6d4 100%)", // token-exempt: brand gradient
            boxShadow: "0 0 22px rgba(168,85,247,0.5), inset 0 0 0 1px rgba(255,255,255,0.2)",
          }}
        >
          <span
            className="absolute rounded-full"
            style={{
              inset: "7px",
              background: "radial-gradient(circle at 30% 30%, #1a1a22, #000)", // token-exempt: brand logo
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
            }}
          />
        </div>
        <span className="font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.035em] text-[var(--iris-fg-1)]">
          Iris Ops
        </span>
      </div>

      {/* Center nav pills */}
      <nav aria-label="Sections" className="flex items-center gap-2.5">
        {sections.map(({ to, label, Icon }) => {
          const active = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
          return (
            <Link
              key={to}
              to={to}
              aria-label={label}
              data-active={active}
              className={cn(
                "relative flex items-center justify-center gap-2.5 rounded-full transition-all",
                active
                  ? "h-11 bg-white pl-[18px] pr-[22px] py-[10px] text-[14px] font-semibold tracking-[-0.01em] text-[var(--iris-bg-0)] shadow-[0_8px_32px_rgba(255,255,255,0.15),0_2px_8px_rgba(232,121,249,0.25),inset_0_1px_0_rgba(255,255,255,0.6)]"
                  : "h-11 pl-[14px] pr-[18px] py-[10px] text-[13px] font-medium tracking-[-0.01em] border border-white/[0.06] bg-[rgba(20,19,24,0.8)] text-[var(--iris-fg-3)] backdrop-blur-[8px] hover:border-[rgba(168,85,247,0.4)] hover:text-[var(--iris-fg-1)] hover:shadow-[0_0_18px_rgba(168,85,247,0.2)]",
              )}
            >
              {!active && (
                <span
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    background: "linear-gradient(180deg, rgba(255,255,255,0.06), transparent 50%)",
                  }}
                />
              )}
              <Icon className="h-[17px] w-[17px]" />
              <span>{label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Right actions (IR-1218: every button here must respond — no
          decorative placeholders; the Search mock-up button was removed) */}
      <div className="flex items-center gap-2.5">
        <NotificationBell />
        <AccountMenu />
      </div>
    </header>
  );
}

function AccountMenu() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account"
          className="h-11 w-11 rounded-full border border-white/[0.1] shadow-[0_0_18px_rgba(168,85,247,0.3)] transition-all hover:border-[rgba(168,85,247,0.6)] hover:shadow-[0_0_22px_rgba(168,85,247,0.45)]"
          style={{
            background: "radial-gradient(circle at 30% 30%, #f0abfc 0%, #a855f7 40%, #7c3aed 100%)", // token-exempt: brand avatar
          }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[240px] border border-white/[0.08] bg-[var(--iris-bg-2)] text-[var(--iris-fg-1)]"
      >
        <DropdownMenuLabel className="normal-case tracking-normal text-[var(--iris-fg-3)]">
          {user?.email ?? "Signed in"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout()}>
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationBell() {
  const { data: unreadData } = useUnreadCount();
  const { data: notifications } = useNotifications(true);
  const markRead = useMarkNotificationsRead();

  const count = unreadData?.count ?? 0;
  const severity = unreadData?.max_severity ?? "info";

  const badgeClass =
    severity === "critical"
      ? "bg-[var(--iris-neon-pink)] shadow-[0_0_8px_var(--iris-neon-pink)]"
      : severity === "warning"
        ? "bg-[var(--iris-neon-orange)] shadow-[0_0_8px_var(--iris-neon-orange)]"
        : "bg-[var(--iris-neon-green)] shadow-[0_0_8px_var(--iris-neon-green)]";

  const recentNotifications = (notifications ?? []).slice(0, 8);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <NavIconButton aria-label="Notifications" className="relative">
          <Bell className="h-[17px] w-[17px]" />
          {count > 0 && (
            <span
              className={cn(
                "absolute right-[10px] top-[9px] flex h-[16px] min-w-[16px] items-center justify-center rounded-full px-[3px] text-[9px] font-bold text-black",
                badgeClass,
              )}
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </NavIconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[320px] border border-white/[0.08] bg-[var(--iris-bg-2)] p-0 text-[var(--iris-fg-1)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
          <span className="text-[13px] font-semibold tracking-[-0.01em]">Notifications</span>
          {count > 0 && (
            <button
              type="button"
              className="text-[11px] text-[var(--iris-fg-3)] transition-colors hover:text-[var(--iris-fg-1)]"
              onClick={() => markRead.mutate([])}
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Notification list */}
        {recentNotifications.length === 0 ? (
          <div className="px-4 py-6 text-center text-[12px] text-[var(--iris-fg-3)]">
            No unread notifications
          </div>
        ) : (
          recentNotifications.map((n) => (
            <DropdownMenuItem
              key={n.id}
              className="flex flex-col items-start gap-0.5 rounded-none border-b border-white/[0.04] px-4 py-3 last:border-b-0 focus:bg-white/[0.04]"
            >
              <span className="text-[12px] font-semibold leading-tight">{n.title}</span>
              <span className="line-clamp-2 text-[11px] leading-snug text-[var(--iris-fg-3)]">
                {n.message}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// IR-1218: must be forwardRef — Radix's DropdownMenuTrigger (asChild) passes
// its popper-anchor ref to this component. A plain function component drops
// that ref on React 18, so Floating UI never gets an anchor and the dropdown
// stays parked at the translate(0, -200%) pre-position placeholder (invisible,
// 2× its height above the viewport).
const NavIconButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  function NavIconButton({ children, className, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          "relative flex h-11 w-11 items-center justify-center rounded-full border border-white/[0.06] bg-[rgba(20,19,24,0.8)] text-[var(--iris-fg-3)] backdrop-blur-[8px] transition-all hover:border-[rgba(168,85,247,0.4)] hover:text-[var(--iris-fg-1)] hover:shadow-[0_0_18px_rgba(168,85,247,0.2)]",
          className,
        )}
        {...rest}
      >
        <span
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.06), transparent 50%)",
          }}
        />
        {children}
      </button>
    );
  },
);
